import { cn } from "@babackapp/ui/lib/utils";
import { Container } from "@react-email/components";
import type { ReactNode } from "react";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import { getThemeRadius } from "@babackapp/blocks-editor/utils/theme-radius";

export interface EmailCardContainerProps {
  children: ReactNode;
  theme: EmailsThemeDto | null | undefined;
  cardColor?: string | null;
  cardTextColor?: string | null;
  isEmailRenderer?: boolean;
  isEditor?: boolean;
  isWeb?: boolean;
  editorClassName?: string;
  /**
   * Max width for the card in the email renderer. When set (e.g. the 680px
   * return layout) it overrides react-email's default `37.5em` so the card
   * fills the outer container. When omitted (tracking) the default applies —
   * no implicit cross-feature override.
   */
  contentMaxWidth?: number;
}

/**
 * Shared card container component for both email renderer and editor
 * Handles:
 * - Card background color
 * - Card text color
 * - Responsive padding: 32px (desktop), 16px (mobile ≤600px)
 * - Card shadow (shadow-lg when enabled)
 * - Card border radius
 * - Card margin: mt-4 mb-1
 */
export function EmailCardContainer({
  children,
  theme,
  cardColor = "#ffffff",
  cardTextColor,
  isEmailRenderer = false,
  isEditor = false,
  isWeb = false,
  editorClassName,
  contentMaxWidth,
}: EmailCardContainerProps) {
  const cardRadius = getThemeRadius(theme, "card");

  const commonClasses = cn("bg-card px-8 py-8 w-full mt-4 mb-1", cardRadius);

  // For email rendering: inline styles with reduced padding on mobile via media queries
  const emailPaddingStyle = {
    padding: "32px", // Desktop fallback
  };

  const commonStyle = {
    backgroundColor: cardColor || undefined,
    color: cardTextColor || undefined,
  };

  if (isEmailRenderer) {
    return (
      <Container
        className={cn(
          "email-card-padding bg-card w-full mt-4 mb-4",
          cardRadius,
        )}
        // Only override react-email <Container>'s default `max-width: 37.5em`
        // when a layout width is provided (return passes 680, which exceeds the
        // parent so the card fills it). Omitted for tracking → default applies.
        style={{
          ...commonStyle,
          ...emailPaddingStyle,
          ...(contentMaxWidth ? { maxWidth: contentMaxWidth } : {}),
        }}
      >
        {children}
      </Container>
    );
  }

  if (isEditor) {
    return (
      <div className={cn("w-full @container", editorClassName)}>
        <div
          className={cn("bg-card p-4 @md:p-8 w-full mt-4 mb-4", cardRadius)}
          style={commonStyle || undefined}
        >
          {children}
        </div>
      </div>
    );
  }

  if (isWeb) {
    return (
      <div className={cn("w-full @container")}>
        <div
          className={cn(commonClasses, "drop-shadow-md")}
          style={commonStyle || undefined}
        >
          {children}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
